<template>
    <div>
        <!-- A link that cannot be opened says so in plain words. It used to stay on "Now redirecting you..." forever
             under the raw axios error ("Request failed with status code 404"). Plain text only (never v-html). -->
        <div v-if="failure" class="booking-link-failure" data-test="booking-link-failure" role="alert">{{ failureMessage }}</div>
        <div v-else>Now redirecting you...</div>
    </div>
</template>

<script>

import { mapActions } from 'vuex';
import { isSiteInactiveResponse } from '@/utils/siteInactiveLink';

const MESSAGES = {
    // The practice behind this link has left Teamio (the API answered 200 'site-inactive'): nothing to retry.
    inactive: 'The practice behind this booking link is no longer using Teamio. Please contact them directly.',
    // The link is not (or no longer) one we serve: a 4xx answer, or an answer with nothing to open.
    invalid: 'This booking link is no longer valid. Please contact the office directly.',
    // Something failed on the way (no connection, a server error): trying again can help.
    retry: "We couldn't open this booking link. Please try again in a moment, or contact the office.",
};

export default {
    name: "BookingRedirect",
    props: ['urlID'],

    async created() {
        try {
            let res = await this.resolveBookingUrl({urlID: this.urlID});
            console.dir(res);

            if (res && res.status === 200 && res.data && res.data.type === 'booking' && res.data.origUrl) {
                const data = res.data;
                const url = new URL(data.origUrl);
                // Merge incoming query params (UTMs, etc.) into the redirect URL
                const targetParams = new URLSearchParams(url.search);
                const incomingParams = new URLSearchParams(window.location.search);
                for (const [key, value] of incomingParams.entries()) {
                    if (!targetParams.has(key)) {
                        targetParams.set(key, value);
                    }
                }
                // Tag with short URL ID so booking page can record which link was used
                targetParams.set('_sid', this.urlID);
                const redirectUrl = `${window.location.origin}${url.pathname}?${targetParams.toString()}`;
                console.log(`Redirecting to booking page: ${redirectUrl}`);
                this.redirectTo(redirectUrl);
                return;
            }
            // Checked after the redirect branch (a real booking link is untouched) and before the generic "not valid".
            this.failure = isSiteInactiveResponse(res) ? 'inactive' : this.failureFor(res);
        } catch(err) {
            console.error(err);
            this.failure = 'retry';
        }
    },

    data: () => ({
        failure: null, // null while resolving, then 'invalid' | 'retry' | 'inactive'
    }),

    methods: {
        ...mapActions('bookingRedirect', ['resolveBookingUrl']),

        redirectTo(url) {
            window.location.href = url;
        },

        // The store answers a failed lookup with { failed: true, status } (status absent when nothing came back).
        failureFor(res) {
            if (res && res.failed) {
                return res.status >= 400 && res.status < 500 ? 'invalid' : 'retry';
            }
            return 'invalid'; // answered 200 but with nothing we can open
        },
    },

    computed: {
        failureMessage() {
            return MESSAGES[this.failure] || '';
        },
    }
}

</script>

<style lang="scss" scoped>
// A patient on a phone must be able to see and read this at a glance: centered, a readable width, 18px text.
.booking-link-failure {
    max-width: 480px;
    // Padding, not a top margin: a margin collapses through the page body and makes the page scroll.
    margin: 0 auto;
    padding: 64px 24px 0;
    text-align: center;
    font-size: 18px;
    line-height: 1.5;
    color: #333;
}
</style>
